import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useSuperAdminAccess } from "@/hooks/useSuperAdminAccess";
import {
  SA_CONTEXT_EVENT,
  clearSaCustomerContext,
  readSaCustomerContext,
  saCompanyPath,
  type SaCustomerContext,
} from "@/lib/sa/customerContext";

/**
 * Persistent banner shown on existing application pages that were opened from
 * Company Intelligence. Reminds the administrator whose data they are viewing
 * and provides breadcrumb + back navigation. Super Admin only.
 */
export function SACustomerContextBar() {
  const [ctx, setCtx] = useState<SaCustomerContext | null>(() => readSaCustomerContext());
  const { isSuperAdmin } = useSuperAdminAccess({ redirect: false });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const sync = () => setCtx(readSaCustomerContext());
    window.addEventListener(SA_CONTEXT_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SA_CONTEXT_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    setCtx(readSaCustomerContext());
  }, [location.pathname, location.search]);

  // Inside Company Intelligence itself the header already provides the context.
  if (!ctx || !isSuperAdmin || location.pathname.startsWith(`/sa/companies/`)) return null;

  return (
    <div className="sticky top-0 z-40 border-b bg-muted/60 backdrop-blur supports-[backdrop-filter]:bg-muted/50">
      <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2">
        <Badge variant="destructive" className="gap-1 font-medium">
          <ShieldCheck className="h-3 w-3" />
          Super Admin
        </Badge>

        <div className="flex min-w-0 flex-wrap items-center gap-1 text-xs text-muted-foreground">
          <button className="hover:text-foreground" onClick={() => navigate("/sa/companies")}>
            Customers
          </button>
          <ChevronRight className="h-3 w-3" />
          <button
            className="max-w-[220px] truncate font-medium text-foreground hover:underline"
            onClick={() => navigate(saCompanyPath(ctx))}
          >
            {ctx.companyName}
          </button>
          {ctx.tabLabel && (
            <>
              <ChevronRight className="h-3 w-3" />
              <button className="hover:text-foreground" onClick={() => navigate(saCompanyPath(ctx))}>
                {ctx.tabLabel}
              </button>
            </>
          )}
          {ctx.recordLabel && (
            <>
              <ChevronRight className="h-3 w-3" />
              <span className="max-w-[200px] truncate text-foreground">{ctx.recordLabel}</span>
            </>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {ctx.plan && (
            <Badge variant="outline" className="hidden font-normal sm:inline-flex">
              {ctx.plan}
            </Badge>
          )}
          {typeof ctx.healthScore === "number" && (
            <Badge variant="secondary" className="hidden font-normal sm:inline-flex">
              Health {ctx.healthScore}
            </Badge>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            onClick={() => navigate(saCompanyPath(ctx))}
          >
            <ArrowLeft className="h-3 w-3" />
            Back to Company Intelligence
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => {
              clearSaCustomerContext();
              setCtx(null);
            }}
          >
            Exit
          </Button>
        </div>
      </div>
    </div>
  );
}
